<template>
    <div>
        <transition name="quick-fade">
            <div class="pickup-time-modal-background" v-if="modalOpen"></div>
        </transition>
        <div class="pickup-time-modal-container">
            <div class="align-middle flex-container" v-if="showOpener">
                <button class="align-middle pickup-time-modal-button flex-container" @click="() => (canBeOpen) ? toggleTimeModal() : false">
                    <span class="opener-grid" v-if="retrievalMethod && canBeOpen">
                        <span class="retrieval-method-label semi-bold">{{ retrievalMethodLabel().toUpperCase() }}<span v-html="Icon.SMALL_ARROW_DOWN" class="opener-arrow"></span></span>
                        <span class="retrieval-time display-xsmall semi-bold">{{ selectedPickupTime }}</span>
                    </span>
                    <span class="opener-grid" v-else>
                        <span class="retrieval-method-label semi-bold">CLOSED</span>
                    </span>
                </button>
            </div>
            <transition name="quick-fade">
                <div class="pickup-time-modal" v-if="modalOpen">
                    <div class="time-modal-logo hide-for-large">
                        <img :src="storeLogo" alt="Store Logo">
                    </div>
                    <div class="image-background layer-fg hide-for-large">
                        <img src="/media/static/svg/time_modal_fg.svg" alt="Time Modal Background Foreground">
                    </div>
                    <div class="image-background layer-bg hide-for-large">
                        <img src="/media/static/svg/time_modal_bg.svg" alt="Time Modal Background">
                    </div>
                    <div class="pickup-time-modal-content">
                        <alert :type="AlertType.WARNING" title="Demand is off the charts!" v-if="displayTimeWarning">
                            <p>Waiting times might be longer than usual.</p>
                        </alert>
                        <alert :type="AlertType.WARNING" title="Online orders unavailable" v-if="availableSlots.length < 1">
                            <p>Sorry, we cannot take any new orders at this time.</p>
                        </alert>
                        <underline-radio
                            dusk="retrieval-methods"
                            v-if="availableSlots.length > 0"
                            :options="retrievalMethodOptions"
                            :selected="retrievalMethod"
                            :disabled-options="disabledRetrievalMethodOptions"
                            @select="retrievalMethod = $event"
                        ></underline-radio>
                        <ul class="time-options-container" v-if="availableSlots.length > 0">
                            <li>
                                <checkbox
                                    :label="asapLabel"
                                    class="pickup-time-option"
                                    :label-left="true"
                                    label-class="time-modal-option semi-bold"
                                    :radio="true"
                                    :disabled="isLoading"
                                    :checked="selectedTimeOption === TimeOption.ASAP"
                                    @change="selectTimeOption(TimeOption.ASAP)"
                                ></checkbox>
                            </li>
                            <li v-if="availableSchedulingOption === AvailableSchedulingOptions.ALL">
                                <checkbox
                                    :label="parsedSelectedTimeSlot"
                                    class="pickup-time-option semi-bold"
                                    :label-left="true"
                                    label-class="time-modal-option semi-bold"
                                    :radio="true"
                                    :disabled="isLoading"
                                    :checked="selectedTimeOption === TimeOption.LATER"
                                    @change="selectTimeOption(TimeOption.LATER)"
                                ></checkbox>
                            </li>
                            <time-slots-list
                                :retrieval-method="retrievalMethod"
                                :selected-time-slot="selectedTimeSlot"
                                :is-open="listOpen"
                                @select="closeList"
                            >
                            </time-slots-list>
                        </ul>
                        <transition name="quick-fade">
                            <p class="text-center display-xsmall semi-bold delivery-label" v-if="deliveryLabel && retrievalMethod === OrderRetrievalMethod.DELIVERY">{{ deliveryLabel }}</p>
                        </transition>
                        <alert :type="AlertType.WARNING" title="Some items will be removed" v-if="willRemoveIngredients">
                            <p>Some items are not available at this time. Continuing will remove them from your cart.</p>
                        </alert>
                        <alert :type="AlertType.WARNING" title="Choose Another Timeslot" v-if="!isRetrievalMethodAvailable">
                            <p>Today's {{ retrievalMethodLabel(retrievalMethod).toLowerCase() }} is from {{ scheduleOpeningHoursLabel(retrievalMethod) }}. Please select a new timeslot within these hours.</p>
                        </alert>
                        <button class="button full-width ordering-button" v-if="canApply" @click="closeModal">{{ (willRemoveIngredients) ? `Change time and remove items from cart` : 'Start ' + retrievalMethodLabel(retrievalMethod).toLowerCase() + ' order' }}</button>
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>
<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import TimeSlotsList from '@/components/front/time-scheduler/TimeSlotsList.vue';
import { getModule } from 'vuex-module-decorators';
import TimeSchedulerModule, { OrderRetrievalMethod, AvailableRetrievalMethods, AvailableSchedulingOptions } from '@/stores/modules/time-scheduler';
import TimeSlot, { TimeSlotState } from '@/models/time-slot';
import Checkbox from '@/components/common/ui/Checkbox.vue';
import UnderlineRadio from '@/components/common/ui/UnderlineRadio.vue';
import { Icon } from '@/front/icons';
import Cookie from '@/utils/cookies';
import CartModule from '@/stores/modules/cart';
import Product from '@/models/product';
import Settings, { SettingNamespace } from '@/utils/settings';
import Alert from '@/components/common/ui/Alert.vue';
import { TimeOption, AlertType } from '@/utils/types';
import { convertTo12HourFormat } from '@/utils/utilities';

@Component({
    components: { TimeSlotsList, Checkbox, UnderlineRadio, Alert }
})
export default class PickupTimeModal extends Vue
{
    @Prop({type: Boolean, default: true}) showOpener!: boolean;

    timeSchedulerModule!: TimeSchedulerModule;
    cartModule!: CartModule;
    listOpen: boolean = false;
    selectedTimeSlot: TimeSlot | null = null;
    selectedTimeOption: TimeOption = TimeOption.ASAP;
    retrievalMethod: OrderRetrievalMethod = OrderRetrievalMethod.PICK_UP;
    minDeliveryFee: number = 0;
    maxDeliveryFee: number = 0;
    deliveryLabel: string = '';
    storeLogo: string = '';
    canBeOpen: boolean = true;

    public created() {
        this.timeSchedulerModule = getModule(TimeSchedulerModule, this.$store);
        this.cartModule = getModule(CartModule, this.$store);
        this.retrievalMethod = this.timeSchedulerModule.retrievalMethod;
    }

    public async mounted() {
        const settings = await Settings.getInstance();
        this.storeLogo = settings.get(SettingNamespace.GENERAL, 'store_logo')?.value;
        this.minDeliveryFee = (settings.get(SettingNamespace.ORDERS, 'min_delivery_fee')!.value) ? settings.get(SettingNamespace.ORDERS, 'min_delivery_fee')!.value : 0;
        this.maxDeliveryFee = (settings.get(SettingNamespace.ORDERS, 'max_delivery_fee')!.value) ? settings.get(SettingNamespace.ORDERS, 'max_delivery_fee')!.value : 0;
        this.deliveryLabel = (settings.get(SettingNamespace.ORDERS, 'delivery_label')!.value) ? settings.get(SettingNamespace.ORDERS, 'delivery_label')!.value : '';
        this.onRetrievalMethodClosed();
        this.$watch('availableRetrievalMethods', this.onAvailableRetrievalMethodsChanged, {immediate: false});
        if (this.canBeOpen && !Cookie.get('visited_today')) {
            this.timeSchedulerModule.setTimeModalOpen(true);
        }
    }

    onRetrievalMethodClosed() {
        const isRetrievalMethodClosed = this.isRetrievalMethodClosed(this.retrievalMethod);
        if (isRetrievalMethodClosed) {
            switch (this.availableRetrievalMethods) {
                case AvailableRetrievalMethods.DELIVERY:
                    if (this.retrievalMethod === OrderRetrievalMethod.DELIVERY) {
                        this.canBeOpen = false;
                    }
                    break;
                case AvailableRetrievalMethods.PICK_UP:
                    if (this.retrievalMethod === OrderRetrievalMethod.PICK_UP) {
                        this.canBeOpen = false;
                    }
                    break;
                default:
                    if (this.areRetrievalMethodsClosed()) {
                        this.canBeOpen = false;
                        break;
                    }
                    if (this.retrievalMethod === OrderRetrievalMethod.PICK_UP) {
                        this.timeSchedulerModule.setRetrievalMethod(OrderRetrievalMethod.DELIVERY);
                    } else if (this.retrievalMethod === OrderRetrievalMethod.DELIVERY) {
                        this.timeSchedulerModule.setRetrievalMethod(OrderRetrievalMethod.PICK_UP);
                    }
                break;
            }
        }
    }

    areRetrievalMethodsClosed() {
        return (this.availableRetrievalMethods === AvailableRetrievalMethods.ALL && this.isRetrievalMethodClosed(OrderRetrievalMethod.DELIVERY) && this.isRetrievalMethodClosed(OrderRetrievalMethod.PICK_UP));
    }

    closeModal() {
        const now = new Date();
        Cookie.set('visited_today', true, new Date(now.getTime() + 1 * 60 * 60 * 1000));
        if (this.selectedTimeOption) {
            this.timeSchedulerModule.setTimeOption(this.selectedTimeOption);
        }
        if (this.timeSchedulerModule.selectedTimeOption === TimeOption.ASAP) {
            this.timeSchedulerModule.setSelectedTimeSlot(this.timeSchedulerModule.asapSlot);
        } else if (this.timeSchedulerModule.selectedTimeOption === TimeOption.LATER && this.selectedTimeSlot) {
            this.timeSchedulerModule.setSelectedTimeSlot(this.selectedTimeSlot);
        }
        for (let i in this.unavailableProducts) {
            this.cartModule.removeProduct(this.unavailableProducts[i]);
        }
        this.timeSchedulerModule.setRetrievalMethod(this.retrievalMethod);
        this.timeSchedulerModule.setTimeModalOpen(false);
        this.timeSchedulerModule.setCurrentTimeSlot();
    }

    closeList(timeSlot: TimeSlot) {
        this.listOpen = false;
        this.selectedTimeSlot = timeSlot;
    }

    cancelClose() {
        this.timeSchedulerModule.setTimeModalOpen(false);
        this.selectedTimeOption = this.timeSchedulerModule.selectedTimeOption;
        this.selectedTimeSlot = this.timeSchedulerModule.selectedTimeSlot;
        this.retrievalMethod = this.timeSchedulerModule.retrievalMethod;
        this.timeSchedulerModule.setCurrentTimeSlot();
    }

    selectTimeOption(selectedOption: TimeOption) {
        this.listOpen = (selectedOption === TimeOption.LATER);
        this.selectedTimeOption = selectedOption;
        if (this.selectedTimeOption === TimeOption.ASAP) {
            this.selectedTimeSlot = this.timeSchedulerModule.asapSlot;
        }
    }

    onAvailableRetrievalMethodsChanged(newRetrievalMethod: AvailableRetrievalMethods) {
        if (newRetrievalMethod === AvailableRetrievalMethods.DELIVERY) {
            this.retrievalMethod = OrderRetrievalMethod.DELIVERY;
        } else if (newRetrievalMethod === AvailableRetrievalMethods.PICK_UP) {
            this.retrievalMethod = OrderRetrievalMethod.PICK_UP;
        }
    }

    async toggleTimeModal() {
        this.timeSchedulerModule.setTimeModalOpen(!this.modalOpen);
        if (this.modalOpen) {
            this.selectedTimeOption = this.timeSchedulerModule.selectedTimeOption;
            this.retrievalMethod = this.timeSchedulerModule.retrievalMethod;
            await this.timeSchedulerModule.fetchTimeSlots();
            this.timeSchedulerModule.setCurrentTimeSlot();

            if (this.selectedTimeOption === TimeOption.ASAP) {
                this.selectedTimeSlot = this.timeSchedulerModule.asapSlot;
            } else {
                this.selectedTimeSlot = this.timeSchedulerModule.selectedTimeSlot;
            }
        }
    }

    get willRemoveIngredients() {
        return this.unavailableProducts.length > 0;
    }

    get unavailableProducts() {
        let unavailProducts: Product[] = [];
        if (this.selectedTimeSlot) {
            const products = this.cartModule.products;
            for (let i in products) {
                for (let index in products[i].ingredients) {
                    const ingredient = products[i].ingredients[index];
                    if (ingredient.time_start && ingredient.time_end) {
                        // Force date in case the data is imported from the local storage
                        ingredient.time_start.setDate(this.selectedTimeSlot.start().getDate());
                        ingredient.time_end.setDate(this.selectedTimeSlot.end().getDate());
                        if (!(this.selectedTimeSlot.start() >= ingredient.time_start && this.selectedTimeSlot.end() < ingredient.time_end)) {
                            unavailProducts.push(products[i]);
                        }
                    }
                }
            }
        }
        return unavailProducts;
    }

    get scheduleOpeningHoursLabel() {
        return (retrievalMethod: OrderRetrievalMethod) => {
            const retrievalMethodSchedule = this.timeSchedulerModule.schedules[retrievalMethod];
            if (retrievalMethodSchedule){
                return convertTo12HourFormat(retrievalMethodSchedule.start_time) + ' to ' + convertTo12HourFormat(retrievalMethodSchedule.end_time);
            }
            return '';
        }
    }

    get canApply() {
        return !this.listOpen && this.isRetrievalMethodAvailable;
    }

    get isLoading() {
        return this.timeSchedulerModule.isLoading;
    }

    get modalOpen() {
        return this.timeSchedulerModule.timeModalOpen;
    }

    get asapLabel() {
        return this.timeSchedulerModule.asapLabel(this.retrievalMethod);
    }

    get selectedPickupTime() {
        return this.timeSchedulerModule.pickupTime;
    }

    get displayTimeWarning() {
        return this.timeSchedulerModule.displayWarning;
    }

    get TimeOption() {
        return TimeOption;
    }

    get availableSchedulingOption() {
        return this.timeSchedulerModule.availableSchedulingOption;
    }

    get AvailableSchedulingOptions() {
        return AvailableSchedulingOptions;
    }

    get availableSlots() {
        return this.timeSchedulerModule.timeSlots.filter((timeSlot: TimeSlot) => timeSlot.state !== TimeSlotState.PAST);
    }

    get parsedSelectedTimeSlot() {
        if (this.selectedTimeSlot && this.selectedTimeOption === TimeOption.LATER) {
            if (this.retrievalMethod === OrderRetrievalMethod.DELIVERY) {
                const start = new Date(this.selectedTimeSlot.start().getTime() + this.timeSchedulerModule.deliveryTime * 60 * 1000);
                const end = new Date(this.selectedTimeSlot.end().getTime() + this.timeSchedulerModule.deliveryTime * 60 * 1000);
                return start.toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true }) + ' to ' + end.toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });
            }
            return this.selectedTimeSlot.parsedStart + ' to ' + this.selectedTimeSlot.parsedEnd;
        }
        return 'Later today';
    }

    get availableRetrievalMethods() {
        return this.timeSchedulerModule.availableRetrievalMethods;
    }

    get retrievalMethodOptions() {
        const pickupLabel = (this.isRetrievalMethodClosed(OrderRetrievalMethod.PICK_UP)) ? 'Pick up closed' : 'Pick up';
        const deliveryLabel = (this.isRetrievalMethodClosed(OrderRetrievalMethod.DELIVERY)) ? 'Delivery closed' : 'Delivery';
        switch (this.availableRetrievalMethods) {
            case AvailableRetrievalMethods.DELIVERY: return [[deliveryLabel, OrderRetrievalMethod.DELIVERY]];
            case AvailableRetrievalMethods.PICK_UP: return [[pickupLabel,OrderRetrievalMethod.PICK_UP]];
            default: return [[pickupLabel, OrderRetrievalMethod.PICK_UP], [deliveryLabel, OrderRetrievalMethod.DELIVERY]];
        }
    }

    get disabledRetrievalMethodOptions() {
        let disabledOptions: any = [];
        for (let retrievalOption of this.retrievalMethodOptions) {
            if (this.isRetrievalMethodClosed(<OrderRetrievalMethod>retrievalOption[1])) {
                disabledOptions.push(retrievalOption[1]);
            }
        }
        return disabledOptions;
    }

    get retrievalMethodLabel() {
        return (retrievalMethod: OrderRetrievalMethod = this.timeSchedulerModule.retrievalMethod) => {
            const labelValue = this.retrievalMethodOptions.find(([label, value]) => retrievalMethod === value);
            if (labelValue !== undefined) {
                return labelValue[0];
            }
            return '';
        }
    }

    get isRetrievalMethodAvailable() {
        return this.timeSchedulerModule.isRetrievalMethodAvailable(this.retrievalMethod, this.selectedTimeSlot);
    }

    get isRetrievalMethodClosed() {
        return (retrievalMethod: OrderRetrievalMethod) => this.timeSchedulerModule.isRetrievalMethodClosed(retrievalMethod);
    }

    get Icon() {
        return Icon;
    }

    get AlertType() {
        return AlertType;
    }

    get OrderRetrievalMethod() {
        return OrderRetrievalMethod;
    }
}
</script>
<style lang="scss">
@import '~foundation-sites/scss/foundation';
@import '~scss/front/settings';

.pickup-time-modal-background {
    @include breakpoint(large) {
        position: fixed;
        top: 74px;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.5);
        backdrop-filter: blur(5px);
    }

}

.pickup-time-modal-container {
    @include breakpoint(large) {
        position: relative;
        padding: 0px 30px;
    }
}

.pickup-time-modal-button {
    padding-left: 4px;

    .opener-grid {
        display: grid;
        grid-template-columns: 1fr;
        padding-right: 15px;
        color: var(--time-modal-opener-color);

        .retrieval-method-label {
            letter-spacing: 1px;
        }

        .opener-arrow {
            position: relative;
            top: -2px;
            padding-left: 5px;

            svg {
                position: relative;

                path {
                    fill: var(--time-modal-opener-arrow-color);
                }
            }
        }

        .retrieval-time {
            position: relative;
        }
    }

}

.pickup-time-modal {
    background: var(--time-modal-bg);
    box-shadow: 3px 6px 13px 0 rgba(0, 0, 0, 0.1);
    z-index: 30;

    @media screen and (max-width: 320px) {
        padding: 30px 30px 12px;
        .title {
            font-size: rem-calc(30) !important;
        }
        .subtitle {
            font-size: rem-calc(26) !important;
        }
    }

    .delivery-label {
        color: rgba(white, 0.8);
    }

    @include breakpoint(medium down) {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        padding: 15px 30px 15px 30px;
        display: flex;
        align-items: flex-end;
        justify-content: center;

        &:before {
            position: absolute;
            content: "";
            z-index: -2;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            background: var(--time-modal-bg-overlay);
        }
    }

    @include breakpoint(large) {
        position: absolute;
        top: var(--time-modal-offset);
        left: 0;
        width: 410px;
        padding: 30px 40px;
        border-radius: var(--time-modal-radius);
    }

    .time-modal-logo {
        position: absolute;
        top: 20px;
        width: 30vw;

        img {
            width: 100%;
        }
    }

    .image-background {
        position: absolute;

        img {
            width: 100%;
        }

        &.layer-fg {
            z-index: -1;
        }

        &.layer-bg {
            z-index: -3;
        }
    }

    .welcome-page {
        width: 100%;

        .title {
            padding-top: 10px;
        }
        .button {
            width: 100%;
        }
    }

    .pickup-time-modal-content {

        .alert {
            margin-bottom: 20px;
        }

        @include breakpoint(medium down) {
            width: 100%;
        }
        &.hidden {
            @include breakpoint(medium down) {
                display: none;
            }
        }
    }

    .time-modal-option {
        color: var(--time-modal-color) !important;
    }

    .ordering-button {
        color: var(--time-modal-button-color);
        background: var(--time-modal-button-bg);
        margin: 0!important;
    }

    .time-options-container {
        list-style-type: none;
        margin: 0;
        margin-bottom: 5px;

        li {

            &:last-of-type {
                .pickup-time-option {
                    border-bottom: none;
                }
            }
        }
    }

    .pickup-time-option {
        padding: 19px 15px;
        position: relative;
        border-bottom: 1px solid var(--time-modal-separator-color);
        cursor: pointer;

        &.selected {
            .custom-checkbox-container {
                &:before {
                    display: block;
                }
            }
        }
    }

    .checkmark {
        background-color: var(--time-modal-checkmark-bg) !important;
        border: 2px solid var(--time-modal-checkmark-border) !important;
    }

    .checkbox-container input:checked ~ .checkmark {
        background-color: var(--time-modal-checkmark-checked-bg) !important;
        border-color: var(--time-modal-checkmark-checked-border) !important;
    }

    .checkbox-container .checkmark:after {
        border-color: var(--time-modal-checkmark-checked-fg) !important;
    }

    .close-button {
        @include breakpoint(medium down) {
            position: absolute;
            top: 10px;
            left: 10px;
            cursor: pointer;
            font-size: 30px;
        }
    }
}


// move to utility classes (useful for position absolute a child)
.position-relative {
    position: relative;
}
</style>
